import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import * as BsIcons from 'react-icons/bs';
import SafeIcon from '../../common/SafeIcon';

const { FiClock, FiArrowLeft, FiHeadphones } = FiIcons;
const { BsSoundwave, BsActivity } = BsIcons;

const AudioEntrainment = () => {
  return (
    <div className="pt-24 pb-20 bg-white">
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <Link to="/resources" className="inline-flex items-center text-slate-500 hover:text-indigo-600 mb-8 transition-colors font-medium">
          <SafeIcon icon={FiArrowLeft} className="mr-2" /> Back to Library
        </Link>

        <header className="mb-12">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <span className="inline-block px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-bold uppercase tracking-wider">
              Sound Therapy
            </span>
            <h1 className="text-4xl md:text-5xl font-black text-slate-900 leading-tight">
              Audio Entrainment Explained: Can Sound Waves Really Upgrade Your Brain?
            </h1>
            <div className="flex items-center gap-4 border-b border-slate-100 pb-8">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-bold">
                SV
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-bold text-slate-900">Sarah Vance</span>
                <span className="text-xs text-slate-500">8 min read</span>
              </div>
            </div>
          </motion.div>
        </header>

        <div className="rounded-3xl overflow-hidden mb-12 shadow-xl">
          <img 
            src="https://cdn1.sharemyimage.com/smi/2026/01/01/scientist-interacts-with-digital-brain-model-hightech-research-lab-setting.jpg" 
            alt="Audio Entrainment Research" 
            className="w-full object-cover"
          />
        </div>

        <div className="prose prose-lg prose-slate max-w-none">
          <p className="lead text-xl text-slate-600 mb-8">
            You've likely heard of "binaural beats" or "isochronic tones." But beyond the YouTube hype, there is legitimate science behind <strong>Brainwave Entrainment</strong>—the practice of using rhythmic stimuli to alter your mental state.
          </p>

          <h3>The Frequency Following Response</h3>
          <p>
            The core principle of audio entrainment is the "Frequency Following Response" (FFR). It's a natural phenomenon where the brain tends to synchronize its electrical cycles (brainwaves) with the rhythm of an external stimulus, such as a pulsing sound or flashing light.
          </p>
          <p>
            Think of it like a drumbeat. If you hear a fast, energetic drumbeat, your heart rate might elevate and you feel alert. If you hear a slow, steady beat, you relax. Entrainment takes this biological shortcut and applies it directly to neural frequencies.
          </p>

          <h3>Decoding Your Brainwaves</h3>
          <p>To understand why this matters, you need to know the four main gears your brain shifts between:</p>

          <div className="grid md:grid-cols-2 gap-6 my-8">
            <div className="bg-slate-50 p-6 rounded-xl border border-slate-100">
              <h4 className="text-indigo-600 font-bold mb-2">Beta (13-30 Hz)</h4>
              <p className="text-sm"><strong>The "Hustle" State.</strong> Active thinking, problem-solving, and focus. Too much leads to anxiety.</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-xl border border-slate-100">
              <h4 className="text-blue-600 font-bold mb-2">Alpha (8-12 Hz)</h4>
              <p className="text-sm"><strong>The "Flow" State.</strong> Relaxed alertness, light meditation, and creative visualization.</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-xl border border-slate-100">
              <h4 className="text-emerald-600 font-bold mb-2">Theta (4-8 Hz)</h4>
              <p className="text-sm"><strong>The "Dream" State.</strong> Deep meditation, REM sleep, and accessing the subconscious.</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-xl border border-slate-100">
              <h4 className="text-purple-600 font-bold mb-2">Gamma (30-100+ Hz)</h4>
              <p className="text-sm"><strong>The "Insight" State.</strong> Peak concentration, binding information together, and "aha!" moments.</p>
            </div>
          </div>

          <h3>Why Gamma Matters Most</h3>
          <p>
            For years, Alpha (relaxation) was the darling of the biohacking world. But recently, <strong>Gamma waves</strong> have taken center stage. Research from MIT has shown that inducing 40Hz Gamma oscillations can reduce the plaque buildup associated with cognitive decline.
          </p>
          <p>
            Gamma is high energy. It's the state your brain is in when you are hyper-focused and processing complex information effortlessly. The problem? It's hard to sustain voluntarily.
          </p>

          <h3>Using Audio to "Tune" Your Brain</h3>
          <p>
            This is where products like <Link to="/review/the-brain-song">The Brain Song</Link> come in. By layering specific frequencies that mimic Gamma waves, these audio tracks act as a "tuning fork" for your brain.
          </p>
          <p>
            Instead of spending 20 minutes trying to meditate your way into focus, entrainment technology attempts to guide your brain there mechanically within minutes. It's not magic; it's physics applied to biology.
          </p>

          <div className="mt-12 p-6 bg-yellow-50 border border-yellow-200 rounded-xl text-yellow-900 text-sm">
            <strong>Safety Note:</strong> While generally safe, audio entrainment should not be used by those with epilepsy or a history of seizures without consulting a doctor.
          </div>
        </div>
      </article>
    </div>
  );
};

export default AudioEntrainment;